// "Update Code" confirmation + progress, shared by all three scopes (device,
// room, location) and both UI shells.
//
// The flow is deliberately three-stage — counting, confirming, sending —
// because this is not undoable and the devices reboot:
//
//   1. COUNT   gather the devices in scope, split live vs offline, and look up
//              what firmware each is on. Offline devices are NOT sent to (they'd
//              drop the message and look updated).
//   2. CONFIRM ONE ROW PER DEVICE — name, firmware file, build date, when it was
//              last updated, and a tick box. Plus the exact payload, so the
//              operator sees the literal bytes before a fleet reflashes itself.
//   3. SEND    publish one device at a time, spaced, with progress — a location
//              rollout takes a while and silence would look like a hang.
//
// ── Why a table and not a summary ──────────────────────────────────────────
// The first version printed counts and a list of filenames. On a location with
// 39 devices that says "4 will update" without ever saying WHICH four, so the
// operator cannot check the one thing that matters before an irreversible
// action. Every device is now a row, and every row is individually selectable:
// the default selection is the safe one, but it is a default, not a decision
// taken on the operator's behalf.
//
// Offline rows are shown, not hidden. "33 offline — skipped" with no names is
// the same failure in miniature; the point of listing them is to notice the one
// that should not be offline.
//
// Rows are grouped the way the building is: COMMON DEVICES first — the gates,
// meters and locks that belong to the location rather than to any room — then
// one section per room. A flat list of thirty-nine devices is a list nobody
// reads; grouped, the operator can tick a whole room, and can see at a glance
// that (say) every device in Parking is offline.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box,
  LinearProgress, CircularProgress, Chip, Alert, Checkbox, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tooltip, Paper,
  FormControlLabel,
} from '@mui/material';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import { useApp } from '../../core/state/AppContext';
import {
  collectLocationDevices, collectRoomDevices, splitByLiveness,
  sendFirmwareUpdate, previewPayload, FIRMWARE_UPDATE_COMMAND, COMMON_ROOM_ID,
} from '../../core/devices/firmwareUpdate';
import {
  firmwareInfoFor, CURRENT, STALE, BUILD_STAMP_TOLERANCE_MS,
} from '../../core/devices/firmwareInfo';

const unoOf = (d) => String((d && (d.UniqueNumber ?? d.uniqueNumber)) ?? '');
const nameOf = (d) => d?.DeviceName || d?.RoomName || unoOf(d) || 'Device';

// Rows are ordered by what the operator has to decide about, not alphabetically:
// the ones that need doing, then the ones deliberately left out, then the ones
// that cannot be done at all.
const RANK = { stale: 0, unknown: 1, current: 2, offline: 3 };

const STATE_CHIP = {
  stale: { label: 'Needs update', color: 'warning' },
  unknown: { label: 'Not known', color: 'default' },
  current: { label: 'Up to date', color: 'success' },
  offline: { label: 'Offline', color: 'default' },
};

const SCOPE_LABEL = {
  device: 'device', room: 'room', location: 'location', list: 'selection',
};

// scope: { type: 'device'|'room'|'location'|'list', id, name, device?,
//          devices?, locationId? }
//
// A 'room' scope should carry locationId — it is what makes the room's device
// list complete (see core/devices/roomMerge.js).
//
// 'list' is an EXPLICIT set of device rows the caller already has, for screens
// whose devices are not a room or a location at all — the Tuya bench, whose
// list is "whatever spoke to the broker most recently" and belongs to no
// building. Everything downstream is device-driven, so the only difference is
// where the rows come from.
export default function UpdateCodeDialog({ open, scope, onClose }) {
  const { userId } = useApp();
  const [phase, setPhase] = useState('counting'); // counting | confirm | sending | done
  const [live, setLive] = useState([]);
  const [offline, setOffline] = useState([]);
  const [method, setMethod] = useState('mqtt');
  const [sent, setSent] = useState(0);
  const [info, setInfo] = useState(null);        // uno -> firmware facts
  const [selected, setSelected] = useState(() => new Set());
  // Two filters, both OFF by default. A location can be 33 offline devices out
  // of 39, and scrolling past all of them to reach the four that matter is the
  // complaint these answer — but hiding by default would decide for the
  // operator what they are allowed to notice.
  const [hideCurrent, setHideCurrent] = useState(false);
  const [hideOffline, setHideOffline] = useState(false);

  const load = useCallback(async () => {
    setPhase('counting');
    setSent(0);
    let devices = [];
    if (scope?.type === 'device') devices = scope.device ? [scope.device] : [];
    else if (scope?.type === 'list') devices = scope.devices || [];
    else if (scope?.type === 'room') {
      devices = await collectRoomDevices(userId, scope.id, scope.locationId, scope.name);
    } else if (scope?.type === 'location') {
      devices = await collectLocationDevices(userId, scope.id);
    }
    // Awaited: liveness is the same cached status the tiles show, so the dialog
    // and the dot can never disagree.
    const split = await splitByLiveness(devices);
    setLive(split.live);
    setOffline(split.offline);
    setMethod(split.method);
    // Only for the ones that could be sent to — an offline device is skipped
    // whatever firmware it is on, so asking about it is a wasted request.
    const facts = await firmwareInfoFor(split.live).catch(() => null);
    setInfo(facts);
    // Default selection: everything live that is not already on this build.
    // A device whose firmware could not be read is INCLUDED — see firmwareInfo,
    // CodeUpdated records an attempt rather than a success, so "don't know"
    // must not quietly mean "skip".
    setSelected(new Set(
      split.live.map(unoOf).filter((u) => facts?.get(u)?.state !== CURRENT),
    ));
    setHideCurrent(false);
    setHideOffline(false);
    setPhase('confirm');
  }, [scope, userId]);

  // ⚠️ KEYED ON WHAT THE SCOPE *IS*, not on the object's identity.
  //
  // Every caller passes an inline `scope={{ … }}` literal, which is a new object
  // on every render. Depending on it directly means: load -> setState -> render
  // -> new literal -> load again, forever. It has not bitten yet only because
  // the screens using it re-render rarely — the Tuya bench re-renders on every
  // MQTT frame, which is once a second on a live rack.
  //
  // The signature covers everything `load` reads, so a genuine scope change
  // still reloads.
  const scopeKey = useMemo(() => JSON.stringify({
    t: scope?.type ?? null,
    id: scope?.id ?? null,
    loc: scope?.locationId ?? null,
    dev: scope?.device ? unoOf(scope.device) : null,
    list: scope?.devices ? scope.devices.map(unoOf) : null,
  }), [scope]);

  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => { if (open) loadRef.current(); }, [open, scopeKey]);

  // One row per device, live and offline together.
  const rows = useMemo(() => {
    const build = (d, isOffline) => {
      const uno = unoOf(d);
      const f = info?.get(uno);
      const state = isOffline ? 'offline' : (f?.state || 'unknown');
      return {
        uno,
        name: nameOf(d),
        device: d,
        offline: isOffline,
        state,
        roomId: d?.RoomID ?? d?.RoomId ?? COMMON_ROOM_ID,
        roomName: String(d?.RoomName || '').trim(),
        binFile: f?.binFile || '',
        binUrl: f?.binUrl || '',
        builtAt: f?.builtAt ?? null,
        flashedAt: f?.flashedAt ?? null,
        withinTolerance: !!f?.withinTolerance,
      };
    };
    return [
      ...live.map((d) => build(d, false)),
      ...offline.map((d) => build(d, true)),
    ];
  }, [live, offline, info]);

  // What the filters leave on screen.
  //
  // ⚠️ EVERYTHING DOWNSTREAM COUNTS FROM THIS, not from `rows`. A hidden row
  // that stayed selected would be sent to without ever being shown — an
  // invisible instruction to reflash, on the one dialog that exists to make the
  // instruction visible. Hiding a row therefore also excludes it from the send.
  const visible = useMemo(() => rows.filter((r) => {
    if (hideOffline && r.offline) return false;
    if (hideCurrent && r.state === CURRENT) return false;
    return true;
  }), [rows, hideCurrent, hideOffline]);

  // Common devices first, then rooms by name. Within a section the ones that
  // need doing come first — with thirty offline devices in a location, sorting
  // a room alphabetically buries the two that matter.
  const groups = useMemo(() => {
    const byRoom = new Map();
    visible.forEach((r) => {
      const key = r.roomName ? `r:${r.roomName.toLowerCase()}` : 'common';
      if (!byRoom.has(key)) {
        byRoom.set(key, { key, title: r.roomName || 'Common devices', common: !r.roomName, rows: [] });
      }
      byRoom.get(key).rows.push(r);
    });
    const out = [...byRoom.values()];
    out.forEach((g) => g.rows.sort(
      (a, b) => (RANK[a.state] - RANK[b.state]) || a.name.localeCompare(b.name),
    ));
    return out.sort((a, b) => {
      if (a.common !== b.common) return a.common ? -1 : 1;
      return a.title.localeCompare(b.title);
    });
  }, [visible]);

  const selectable = visible.filter((r) => !r.offline);
  const onScreen = new Set(visible.map((r) => r.uno));
  const targets = live.filter((d) => selected.has(unoOf(d)) && onScreen.has(unoOf(d)));
  const currentCount = rows.filter((r) => r.state === CURRENT).length;

  const toggle = (uno) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(uno)) next.delete(uno); else next.add(uno);
    return next;
  });

  const allSelected = selectable.length > 0 && selectable.every((r) => selected.has(r.uno));
  const someSelected = selectable.some((r) => selected.has(r.uno));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectable.map((r) => r.uno)));

  // A whole room at a time — the unit a rollout is actually planned in.
  const toggleGroup = (group) => {
    const ids = group.rows.filter((r) => !r.offline).map((r) => r.uno);
    if (!ids.length) return;
    const on = !ids.every((u) => selected.has(u));
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((u) => (on ? next.add(u) : next.delete(u)));
      return next;
    });
  };

  const run = async () => {
    setPhase('sending');
    await sendFirmwareUpdate(targets, userId, (n) => setSent(n));
    setPhase('done');
  };

  const when = (ms) => (ms == null ? '—'
    : new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }));

  const scopeLabel = SCOPE_LABEL[scope?.type] || 'selection';

  const payload = previewPayload(
    scope?.type === 'device' ? (scope.device && scope.device.UniqueNumber) : null,
    userId,
  );

  const cell = { py: 0.75 };

  return (
    <Dialog
      open={open}
      onClose={phase === 'sending' ? undefined : onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <SystemUpdateAltIcon />
        <Box>
          <Typography component="div" sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2 }}>
            Update Code
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {scope?.name ? `${scope.name} · ${scopeLabel}` : scopeLabel}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {phase === 'counting' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">
              Asking the devices if they are there, and what firmware they are on…
            </Typography>
          </Box>
        )}

        {phase === 'confirm' && (
          <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5, alignItems: 'center' }}>
              <Chip
                color={targets.length ? 'warning' : 'default'}
                size="small"
                label={`${targets.length} selected`}
              />
              {currentCount > 0 && (
                <Chip size="small" color="success" variant="outlined" label={`${currentCount} up to date`} />
              )}
              {offline.length > 0 && (
                <Chip size="small" variant="outlined" label={`${offline.length} offline`} />
              )}
              <Box sx={{ flex: 1 }} />
              {/* Filters, not selections: they change what is LISTED, and a row
                  that is not listed is not sent to. */}
              <FormControlLabel
                sx={{ mr: 0 }}
                control={(
                  <Checkbox
                    size="small"
                    checked={hideCurrent}
                    disabled={currentCount === 0}
                    onChange={(e) => setHideCurrent(e.target.checked)}
                  />
                )}
                label={(
                  <Typography variant="caption">
                    {`Ignore up to date${currentCount ? ` (${currentCount})` : ''}`}
                  </Typography>
                )}
              />
              <FormControlLabel
                sx={{ mr: 0 }}
                control={(
                  <Checkbox
                    size="small"
                    checked={hideOffline}
                    disabled={offline.length === 0}
                    onChange={(e) => setHideOffline(e.target.checked)}
                  />
                )}
                label={(
                  <Typography variant="caption">
                    {`Ignore offline${offline.length ? ` (${offline.length})` : ''}`}
                  </Typography>
                )}
              />
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Tick a row to include it — offline devices cannot be sent to.
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360, mb: 1.5 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        disabled={selectable.length === 0}
                        onChange={toggleAll}
                        inputProps={{ 'aria-label': 'Select all devices' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Device</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Firmware file</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Built</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Last updated</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Everything is filtered out — untick a box above to see
                          the rest.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {groups.map((g) => {
                    // One section means the title already names it, and a header
                    // repeating the room would be noise above its own rows.
                    const showHeader = groups.length > 1;
                    const gRows = g.rows.filter((r) => !r.offline).map((r) => r.uno);
                    const gAll = gRows.length > 0 && gRows.every((u) => selected.has(u));
                    const gSome = gRows.some((u) => selected.has(u));
                    const gOff = g.rows.length - gRows.length;
                    return (
                    <React.Fragment key={g.key}>
                      {/* One section header per room. Sticky headers already
                          hold the column names, so this only has to say WHERE
                          the next few rows are. */}
                      {showHeader && (
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={gAll}
                            indeterminate={gSome && !gAll}
                            disabled={gRows.length === 0}
                            onChange={() => toggleGroup(g)}
                            inputProps={{ 'aria-label': `Select all in ${g.title}` }}
                          />
                        </TableCell>
                        <TableCell colSpan={5} sx={{ py: 0.5 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 12 }}
                          >
                            {g.title}
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 1, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}
                            >
                              {`${g.rows.length} device${g.rows.length === 1 ? '' : 's'}`}
                              {gOff > 0 ? ` · ${gOff} offline` : ''}
                            </Typography>
                          </Typography>
                        </TableCell>
                      </TableRow>
                      )}
                      {g.rows.map((r) => {
                    const chip = STATE_CHIP[r.state] || STATE_CHIP.unknown;
                    const checked = selected.has(r.uno);
                    return (
                      <TableRow
                        key={r.uno}
                        hover={!r.offline}
                        onClick={r.offline ? undefined : () => toggle(r.uno)}
                        sx={{
                          cursor: r.offline ? 'default' : 'pointer',
                          opacity: r.offline ? 0.55 : 1,
                        }}
                      >
                        <TableCell padding="checkbox" sx={cell}>
                          <Checkbox
                            size="small"
                            checked={checked}
                            disabled={r.offline}
                            onChange={() => toggle(r.uno)}
                            onClick={(e) => e.stopPropagation()}
                            inputProps={{ 'aria-label': `Update ${r.name}` }}
                          />
                        </TableCell>
                        <TableCell sx={cell}>
                          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                            {r.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {r.uno}
                          </Typography>
                        </TableCell>
                        {/* Plain monospace, not a link: this is the one string
                            on the sheet that must be read character by character,
                            and link colouring made it the least legible thing
                            on it. The URL is on hover for anyone who wants it. */}
                        <TableCell sx={cell}>
                          {r.binFile ? (
                            <Tooltip title={r.binUrl}>
                              <Typography
                                variant="body2"
                                sx={{ fontFamily: 'monospace', fontSize: 12.5, wordBreak: 'break-all' }}
                              >
                                {r.binFile}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }}>
                          <Typography variant="body2">{when(r.builtAt)}</Typography>
                        </TableCell>
                        <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }}>
                          <Typography variant="body2">{when(r.flashedAt)}</Typography>
                        </TableCell>
                        <TableCell sx={cell}>
                          {/* A row reading "Up to date" beside a flash time two
                              minutes OLDER than the build looks like a bug, so
                              say which of the two clocks is responsible. */}
                          <Tooltip
                            title={r.withinTolerance
                              ? `Flashed ${Math.max(1, Math.round((r.builtAt - r.flashedAt) / 60000))}`
                                + ' min before the build was stamped. The upload tool records'
                                + ' the date a few minutes after writing the file, so anything'
                                + ` within ${BUILD_STAMP_TOLERANCE_MS / 60000} minutes counts as the same build.`
                              : ''}
                          >
                            <Chip
                              size="small"
                              label={chip.label}
                              color={chip.color}
                              variant={r.state === STALE ? 'filled' : 'outlined'}
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                    </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* The exact bytes — last look before a fleet reflashes itself. */}
            <Alert severity="warning" sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Command published to each selected device:
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', fontWeight: 700, wordBreak: 'break-all' }}
              >
                {payload}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                They download new firmware and reboot. This cannot be undone.
              </Typography>
            </Alert>

            {/* When the count is zero, say HOW it was counted. A bare "0 online"
                next to a device the user can see working is the report that
                sent us chasing this twice. */}
            {live.length === 0 && (
              <Typography variant="body2" color="error">
                {method === 'mqtt'
                  ? `No device in this ${scopeLabel} answered, and none has reported `
                    + 'to the server in the last minute. A device that is powered and '
                    + 'on the network replies in under a second — check power and '
                    + 'Wi-Fi, then reopen this.'
                  : 'The broker could not be reached, so this fell back to the last '
                    + 'status the server recorded, and none of these devices is '
                    + 'recent. Check this device’s own connection first.'}
              </Typography>
            )}

            {/* Nothing selected because everything is current is a different
                outcome from nothing being online, and needs different words:
                there is nothing wrong and nothing to do. */}
            {live.length > 0 && targets.length === 0 && selectable.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                Nothing is selected. Every device shown was updated after its build
                was made — tick one anyway if you are re-flashing one whose update
                failed, since a failed update still records the attempt.
              </Typography>
            )}

            {method === 'status' && live.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Broker unreachable — this list is from the server’s last recorded
                status, not from the devices themselves.
              </Typography>
            )}
          </>
        )}

        {(phase === 'sending' || phase === 'done') && (
          <Box sx={{ py: 1 }}>
            {/* Against `targets`, not `live`: with devices deselected the two
                differ, and a bar that never fills reads as a stall. */}
            <LinearProgress
              variant="determinate"
              value={targets.length ? (sent / targets.length) * 100 : 100}
              sx={{ mb: 1 }}
            />
            <Typography variant="body2">
              {phase === 'done'
                ? `Sent to ${sent} device${sent === 1 ? '' : 's'}.`
                : `Sending… ${sent} of ${targets.length}`}
            </Typography>
            {phase === 'done' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Devices download and reflash on their own; they will drop offline
                briefly while they do. There is no acknowledgement, so check the
                online dots in a minute or two.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {phase === 'confirm' && <Button onClick={onClose}>Cancel</Button>}
        {phase === 'confirm' && (
          <Button variant="contained" color="warning" disabled={targets.length === 0} onClick={run}>
            {`Update ${targets.length} device${targets.length === 1 ? '' : 's'}`}
          </Button>
        )}
        {phase === 'done' && <Button variant="contained" onClick={onClose}>Close</Button>}
        {phase === 'counting' && <Button onClick={onClose}>Cancel</Button>}
      </DialogActions>
    </Dialog>
  );
}

export { FIRMWARE_UPDATE_COMMAND };
